/**
 * Tarifs de main d'œuvre et de déplacement (lot U3c, ADR-0016, registre B31).
 *
 * Trois portées, la plus précise l'emporte : le site, puis le client, puis toute l'entreprise. Chaque tarif est DATÉ : en
 * poser un nouveau s'ajoute à l'historique, l'ancien reste lisible ; une faute de frappe s'archive (« elle n'a jamais
 * existé »). Un montant laissé vide « hérite » de la portée au-dessus. AUCUNE valeur n'est jamais proposée : « non
 * paramétré » s'affiche tel quel, en orange, et l'intervention chiffrée le dit au lieu de deviner.
 *
 * Le bloc « Quels tarifs pour ce site, tel jour ? » montre, sans rien écrire, ce que le chiffrage retiendrait et d'où cela vient.
 *
 * DROITS (affichage seulement : l'API décide) : tout l'écran est financier. Sans `pricing:read` il n'affiche rien et ne demande
 * rien à l'API ; définir ou archiver exige `pricing:manage` ET `pricing:read`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  api,
  type ChampTarifMo,
  type Party,
  type PorteeTarifMo,
  type Site,
  type TarifMo,
  type TarifsDuJourVue,
} from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { Champ, ErreurFormulaire, Panneau } from '../composants/Formulaire';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { dateFrTarif } from '../domaine/tarifs';
import {
  CHAMPS,
  PHRASE_NUIT,
  effetModification,
  formulaireDepuisTarif,
  formulaireVide,
  libelleCategorie,
  libelleChamp,
  libellePortee,
  montantAffiche,
  phraseTarifRetenu,
  validerFormulaire,
  type FormulaireTarif,
} from '../domaine/tarifs-mo';

function cibleDe(t: TarifMo): string {
  if (t.portee === 'SITE') return t.siteName ?? 'Site';
  if (t.portee === 'PARTY') return t.partyName ?? 'Client';
  return '—';
}

export function TarifsMoPage() {
  const droits = useDroitsGammes();
  const peutVoir = droits.peutVoirPrix;
  const peutEcrire = droits.peutEcrirePrix;

  const [tarifs, setTarifs] = useState<readonly TarifMo[] | null>(null);
  const [archives, setArchives] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [clients, setClients] = useState<readonly Party[]>([]);
  const [sites, setSites] = useState<readonly Site[]>([]);
  // `'nouveau'` : définir un tarif ; un tarif : « Modifier » (le formulaire part de lui) ; `null` : fermé.
  const [panneau, setPanneau] = useState<'nouveau' | TarifMo | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [rechargement, setRechargement] = useState(0);

  useEffect(() => {
    if (!peutVoir) return;
    let annule = false;
    api
      .tarifsMo(archives)
      .then((r) => {
        if (annule) return;
        setTarifs(r.items);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement des tarifs impossible.');
      });
    return () => {
      annule = true;
    };
  }, [peutVoir, archives, rechargement]);

  // Les clients et les sites ne servent qu'aux choix du formulaire et du vérificateur : lus une fois.
  useEffect(() => {
    if (!peutVoir) return;
    let annule = false;
    Promise.all([api.parties(undefined, 'CUSTOMER'), api.sites()])
      .then(([p, s]) => {
        if (annule) return;
        setClients(p.filter((x) => x.status !== 'ARCHIVED').sort((a, b) => a.legalName.localeCompare(b.legalName, 'fr')));
        setSites(s.filter((x) => x.status !== 'ARCHIVED').sort((a, b) => a.name.localeCompare(b.name, 'fr')));
      })
      .catch(() => {
        /* les choix restent vides : le formulaire refuse alors une portée sans cible, rien n'est deviné */
      });
    return () => {
      annule = true;
    };
  }, [peutVoir]);

  const archiver = useCallback(async (t: TarifMo) => {
    const ok = window.confirm(
      `Archiver ce tarif (${t.portee === 'TENANT' ? libellePortee(t.portee) : `${libellePortee(t.portee)} — ${cibleDe(t)}`}, depuis le ${dateFrTarif(t.validFrom)}) ?\n\n` +
        'Il sera écarté comme s’il n’avait jamais existé et le précédent redeviendra en vigueur. ' +
        'À utiliser pour une faute de saisie. DÉFINITIF : pour changer de tarif, définis-en un nouveau, daté.',
    );
    if (!ok) return;
    setErreurAction(null);
    setSucces(null);
    try {
      await api.archiverTarifMo(t.id);
      setRechargement((n) => n + 1);
      setSucces('Tarif archivé.');
    } catch (e) {
      setErreurAction(e instanceof Error ? e : new Error('Archivage impossible.'));
    }
  }, []);

  if (!peutVoir) {
    return (
      <div>
        <div className="entete-page">
          <div>
            <h1>Tarifs de main d’œuvre</h1>
            <p>Le taux horaire de la main d’œuvre et le déplacement, par entreprise, client ou site.</p>
          </div>
        </div>
        <div className="message info">
          Cet écran est réservé aux personnes qui voient les prix : les tarifs de main d’œuvre sont des informations
          financières.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="entete-page">
        <div>
          <h1>Tarifs de main d’œuvre</h1>
          <p>
            Le taux horaire de la main d’œuvre (jour ouvré, nuit, week-end, jour férié) et le déplacement. La portée la plus
            précise l’emporte : le site, puis le client, puis toute l’entreprise. Un montant laissé vide reprend celui de la
            portée au-dessus. Aucun tarif n’est jamais choisi à ta place : tant qu’il n’est pas paramétré, le chiffrage d’une
            intervention le dit.
          </p>
        </div>
        {peutEcrire && (
          <button onClick={() => setPanneau('nouveau')}>Définir un tarif</button>
        )}
      </div>

      {!peutEcrire && (
        <div className="message info">
          Vous consultez les tarifs en lecture seule : en définir ou en archiver demande le droit de gérer les prix.
        </div>
      )}
      {erreur && <div className="message erreur">{erreur}</div>}
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}

      <div className="carte">
        <h2>Les tarifs</h2>
        <div className="carte-corps">
          <label className="case-a-cocher">
            <input type="checkbox" checked={archives} onChange={(e) => setArchives(e.target.checked)} /> Afficher les tarifs
            archivés
          </label>
          {tarifs === null ? (
            <p className="aide">Chargement…</p>
          ) : tarifs.length === 0 ? (
            <div className="vide">
              Aucun tarif n’est paramétré. Tant que c’est le cas, le chiffrage d’une intervention affiche « Tarif non
              paramétré » pour la main d’œuvre et le déplacement.
            </div>
          ) : (
            <table className="tableau-defilant">
              <thead>
                <tr>
                  <th>Portée</th>
                  <th>Pour</th>
                  <th>Depuis le</th>
                  {CHAMPS.map((c) => (
                    <th key={c}>{libelleChamp(c)}</th>
                  ))}
                  <th>Remarque</th>
                  {peutEcrire && <th />}
                </tr>
              </thead>
              <tbody>
                {tarifs.map((t) => (
                  <tr key={t.id} className={t.archived ? 'ligne-remplacee' : ''}>
                    <td>{libellePortee(t.portee)}</td>
                    <td>{cibleDe(t)}</td>
                    <td>{dateFrTarif(t.validFrom)}</td>
                    {CHAMPS.map((c) => (
                      <td key={c} className={t[c] === null ? 'aide' : ''}>
                        {t[c] === null ? 'hérite' : montantAffiche(t[c])}
                      </td>
                    ))}
                    <td>{t.note ?? ''}</td>
                    {peutEcrire && (
                      <td>
                        {t.archived ? (
                          <span className="aide">archivé</span>
                        ) : (
                          <div className="actions-tarif">
                            <button className="secondaire" onClick={() => setPanneau(t)}>
                              Modifier
                            </button>
                            <button className="secondaire" onClick={() => void archiver(t)}>
                              Archiver
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Verificateur sites={sites} rechargement={rechargement} />

      {panneau !== null && (
        <FormulaireTarifMo
          clients={clients}
          sites={sites}
          depuis={panneau === 'nouveau' ? null : panneau}
          onFermer={() => setPanneau(null)}
          onEnregistre={(message) => {
            setPanneau(null);
            setRechargement((n) => n + 1);
            setSucces(message);
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ vérificateur

function Verificateur({ sites, rechargement }: { sites: readonly Site[]; rechargement: number }) {
  const [siteId, setSiteId] = useState('');
  const [jour, setJour] = useState(aujourdhuiLocal());
  const [resultat, setResultat] = useState<TarifsDuJourVue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) {
      setResultat(null);
      return;
    }
    let annule = false;
    api
      .tarifsDuJour(siteId === '' ? null : siteId, jour)
      .then((r) => {
        if (annule) return;
        setResultat(r);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Lecture impossible.');
      });
    return () => {
      annule = true;
    };
  }, [siteId, jour, rechargement]);

  return (
    <div className="carte">
      <h2>Quels tarifs pour ce site, tel jour ?</h2>
      <div className="carte-corps">
        <p className="aide">
          Ce que le chiffrage retiendrait, et d’où cela vient. N’écrit rien. Sans site choisi, on lit les tarifs de toute
          l’entreprise.
        </p>
        <div className="champs-ligne">
          <Champ libelle="Site">
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Aucun (toute l’entreprise)</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Jour">
            <input type="date" value={jour} onChange={(e) => setJour(e.target.value)} />
          </Champ>
        </div>
        {erreur && <div className="message erreur">{erreur}</div>}
        {resultat && (
          <table className="tableau-defilant">
            <tbody>
              <tr>
                <th scope="row">Ce jour-là</th>
                <td>{libelleCategorie(resultat.categorie)}</td>
              </tr>
              <tr>
                <th scope="row">Main d’œuvre retenue</th>
                <td className={resultat.horaire.montant === null ? 'texte-attention' : ''}>
                  {phraseTarifRetenu(resultat.horaire)} <span className="aide">— {libelleChamp(resultat.champHoraire)}</span>
                </td>
              </tr>
              <tr>
                <th scope="row">Déplacement retenu</th>
                <td className={resultat.deplacement.montant === null ? 'texte-attention' : ''}>
                  {phraseTarifRetenu(resultat.deplacement)}
                </td>
              </tr>
              {CHAMPS.map((c: ChampTarifMo) => (
                <tr key={c}>
                  <th scope="row" className="aide">
                    {libelleChamp(c)}
                  </th>
                  <td className={resultat.tous[c].montant === null ? 'texte-attention' : 'aide'}>
                    {phraseTarifRetenu(resultat.tous[c])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="aide">{PHRASE_NUIT}</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ formulaire

function FormulaireTarifMo({
  clients,
  sites,
  depuis,
  onFermer,
  onEnregistre,
}: {
  clients: readonly Party[];
  sites: readonly Site[];
  /** « Modifier » : le tarif d'origine ; `null` pour un tarif neuf. */
  depuis: TarifMo | null;
  onFermer: () => void;
  onEnregistre: (message: string) => void;
}) {
  const [f, setF] = useState<FormulaireTarif>(() => (depuis ? formulaireDepuisTarif(depuis) : formulaireVide(aujourdhuiLocal())));
  const effet = f.remplace !== null && /^\d{4}-\d{2}-\d{2}$/.test(f.validFrom) ? effetModification(f.remplace.validFrom, f.validFrom) : null;
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const montant = (champ: ChampTarifMo, valeur: string) => setF((x) => ({ ...x, montants: { ...x.montants, [champ]: valeur } }));
  const portees: readonly PorteeTarifMo[] = useMemo(() => ['TENANT', 'PARTY', 'SITE'], []);

  const enregistrer = async () => {
    const controle = validerFormulaire(f);
    if (!controle.ok) {
      setErreur(new Error(controle.message));
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      await api.definirTarifMo(controle.entree);
      onEnregistre(
        effet?.genre === 'CORRECTION'
          ? 'Tarif corrigé : l’ancien est archivé (il reste visible avec « Afficher les tarifs archivés »).'
          : effet?.genre === 'CHANGEMENT'
            ? `Nouveau tarif à partir du ${dateFrTarif(f.validFrom)} : l’ancien reste valable jusqu’à la veille.`
            : 'Tarif enregistré. Les tarifs précédents restent lisibles dans l’historique.',
      );
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={depuis ? 'Modifier un tarif' : 'Définir un tarif'}
      sousTitre={
        depuis
          ? 'Garde la même date d’effet pour corriger une faute : l’ancien sera archivé. Mets une date plus tard pour un changement de prix : l’ancien restera valable jusqu’à la veille.'
          : 'Il s’ajoute à l’historique, daté : les tarifs précédents restent lisibles. Un montant laissé vide reprend celui de la portée au-dessus.'
      }
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void enregistrer()} disabled={enCours || effet?.genre === 'INVALIDE'}>
            {enCours ? 'Enregistrement…' : effet?.genre === 'CORRECTION' ? 'Corriger le tarif' : 'Enregistrer le tarif'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        {effet && <div className={effet.genre === 'INVALIDE' ? 'message erreur' : 'message info'}>{effet.phrase}</div>}

        <Champ libelle="Pour qui ?" requis {...(depuis ? { aide: 'La portée et la cible ne changent pas quand on modifie un tarif.' } : {})}>
          <select value={f.portee} disabled={depuis !== null} onChange={(e) => setF({ ...f, portee: e.target.value as PorteeTarifMo })}>
            {portees.map((p) => (
              <option key={p} value={p}>
                {libellePortee(p)}
              </option>
            ))}
          </select>
        </Champ>

        {f.portee === 'PARTY' && (
          <Champ libelle="Client" requis>
            <select value={f.partyId} disabled={depuis !== null} onChange={(e) => setF({ ...f, partyId: e.target.value })}>
              <option value="">Choisir un client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                </option>
              ))}
            </select>
          </Champ>
        )}

        {f.portee === 'SITE' && (
          <Champ libelle="Site" requis>
            <select value={f.siteId} disabled={depuis !== null} onChange={(e) => setF({ ...f, siteId: e.target.value })}>
              <option value="">Choisir un site…</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Champ>
        )}

        {CHAMPS.map((c) => (
          <Champ
            key={c}
            libelle={`${libelleChamp(c)} (€ HT${c === 'travel' ? ', par passage' : ' de l’heure'})`}
            {...(c === 'laborNight' ? { aide: PHRASE_NUIT } : {})}
          >
            <input
              type="text"
              inputMode="decimal"
              placeholder="Vide : hérite"
              value={f.montants[c]}
              onChange={(e) => montant(c, e.target.value)}
            />
          </Champ>
        ))}

        <Champ libelle="Date d’effet" requis aide="Le tarif s’applique aux jours à partir de cette date.">
          <input type="date" value={f.validFrom} onChange={(e) => setF({ ...f, validFrom: e.target.value })} />
        </Champ>

        <Champ libelle="Remarque">
          <input type="text" maxLength={500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Champ>
      </form>
    </Panneau>
  );
}
